import {formatDateRange} from '../../../../../shared/utils/formatDateRange';
import {PERIOD_LABELS, PERIOD_NAMES} from '../constants/period';
import {differenceInDays, isAfter} from 'date-fns';
import {CIRCLE_STATUSES, STATUS_VALUES} from '../constants/status';
import {every, get, isEmpty, some, sortBy} from 'lodash';
import {ServiceTypesDictDto} from '../../../../../shared/types/api-v2.types';

const DONE_LIKE_STATUSES: ReadonlySet<string> = new Set([STATUS_VALUES.DONE]);

export const renderPeriodValue =
  (customPeriodStart: string, customPeriodEnd: string) => (value: keyof typeof PERIOD_NAMES) => {
    return value === PERIOD_NAMES.MANUAL ? formatDateRange(customPeriodStart, customPeriodEnd) : PERIOD_LABELS[value];
  };

export type CircleStatus = (typeof CIRCLE_STATUSES)[keyof typeof CIRCLE_STATUSES];

export interface StatusMapping<T> {
  getStatus: (item: T) => string | undefined;
  getPlannedDate: (item: T) => string | Date | undefined | null;
}

export function getRequestCircleStatus<T>(request: T, mapping: StatusMapping<T>): CircleStatus {
  const status = mapping.getStatus(request);
  const plannedDate = mapping.getPlannedDate(request);

  if (status && DONE_LIKE_STATUSES.has(status)) return CIRCLE_STATUSES.DONE;
  const planned = getValidDate(plannedDate);
  if (!planned) return CIRCLE_STATUSES.EMPTY;

  const now = new Date();

  if (status === STATUS_VALUES.EXECUTED && !isAfter(now, planned)) {
    return CIRCLE_STATUSES.COMPLETED_NOT_CLOSED;
  }

  if (status !== STATUS_VALUES.EXECUTED && isAfter(now, planned)) return CIRCLE_STATUSES.OVERDUE_EXISTS;

  const days = differenceInDays(planned, now);

  if (status !== STATUS_VALUES.EXECUTED && !DONE_LIKE_STATUSES.has(status || '') && days >= 0 && days < 14) {
    return CIRCLE_STATUSES.EXPIRING_SOON;
  }

  if ((status === STATUS_VALUES.READY_TO_PROCESS || status === STATUS_VALUES.IN_PROGRESS) && days > 14) {
    return CIRCLE_STATUSES.ACTIVE_NO_URGENCY;
  }

  return CIRCLE_STATUSES.EMPTY;
}

export function getContractCircleStatus<T>(
  data: T[] | {serviceRequests?: T[]},
  mapping: StatusMapping<T>,
): CircleStatus {
  const requests: T[] = Array.isArray(data) ? data : get(data, 'serviceRequests', []);
  if (isEmpty(requests)) return CIRCLE_STATUSES.EMPTY;

  if (every(requests, (r) => DONE_LIKE_STATUSES.has(mapping.getStatus(r) || ''))) return CIRCLE_STATUSES.DONE;

  if (some(requests, (r) => isOverdue(r, mapping))) return CIRCLE_STATUSES.OVERDUE_EXISTS;
  if (some(requests, (r) => isExpiringSoon(r, mapping))) return CIRCLE_STATUSES.EXPIRING_SOON;
  if (some(requests, (r) => isSafeZone(r, mapping))) return CIRCLE_STATUSES.ACTIVE_NO_URGENCY;
  if (some(requests, (r) => isExecutedInTime(r, mapping))) return CIRCLE_STATUSES.COMPLETED_NOT_CLOSED;

  return CIRCLE_STATUSES.EMPTY;
}

function getValidDate(value: string | Date | undefined | null): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isOverdue<T>(req: T, mapping: StatusMapping<T>): boolean {
  const status = mapping.getStatus(req);
  if (status === STATUS_VALUES.EXECUTED || DONE_LIKE_STATUSES.has(status || '')) return false;
  const date = getValidDate(mapping.getPlannedDate(req));
  return Boolean(date && isAfter(new Date(), date));
}

function isExpiringSoon<T>(req: T, mapping: StatusMapping<T>): boolean {
  const status = mapping.getStatus(req);
  if (status === STATUS_VALUES.EXECUTED || DONE_LIKE_STATUSES.has(status || '')) return false;
  const date = getValidDate(mapping.getPlannedDate(req));
  if (!date) return false;
  const days = differenceInDays(date, new Date());
  return days >= 0 && days < 14;
}

function isSafeZone<T>(req: T, mapping: StatusMapping<T>): boolean {
  const status = mapping.getStatus(req);
  if (status !== STATUS_VALUES.READY_TO_PROCESS && status !== STATUS_VALUES.IN_PROGRESS) return false;
  const date = getValidDate(mapping.getPlannedDate(req));
  if (!date) return false;
  return differenceInDays(date, new Date()) > 14;
}

function isExecutedInTime<T>(req: T, mapping: StatusMapping<T>): boolean {
  const status = mapping.getStatus(req);
  if (status !== STATUS_VALUES.EXECUTED) return false;
  const date = getValidDate(mapping.getPlannedDate(req));
  if (!date) return false;
  return !isAfter(new Date(), date);
}

export const prepareDashboardTabs = (content: ServiceTypesDictDto[]) => {
  return sortBy(content, (item) => item.sortOrder).map((item) => ({
    label: get(item, 'ui_name', '') || get(item, 'name', ''),
    value: get(item, 'code', ''),
  }));
};
